import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ImageSize, resolveAvailableSize } from '@simOn/common/upload-image';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { MediaApiService } from './media-api.service';

/** Minimal attachment shape needed to resolve a preview url. */
export type ThumbnailAttachment = {
  blobPath?: string;
  urlWithToken?: string;
  availableSizes?: string;
  isBlockedBySubscriptionLimit?: boolean;
};

/**
 * Resolves a displayable url for an attachment image, the same way `common-image-item` does:
 * SAS urls are used directly, otherwise a download link is generated and the file is fetched
 * as a blob (the link needs the API auth headers) and exposed as an object url.
 * Object urls must be released with `revoke` when the consumer is destroyed.
 */
@Injectable({ providedIn: 'root' })
export class AttachmentThumbnailService {
  private readonly _http = inject(HttpClient);
  private readonly _mediaApi = inject(MediaApiService);

  /** Emits '' when the attachment has no usable source or loading fails. */
  thumbnailUrl$(
    attachment: ThumbnailAttachment | null | undefined,
    size: ImageSize = 'Small'
  ): Observable<string> {
    if (!attachment || attachment.isBlockedBySubscriptionLimit) return of('');
    if (attachment.urlWithToken) return of(attachment.urlWithToken);
    if (!attachment.blobPath) return of('');
    return this._mediaApi
      .generateBlobUrlForImageDownload({
        url: attachment.blobPath,
        availableSizes: attachment.availableSizes,
        size: resolveAvailableSize(attachment.availableSizes, size)
      })
      .pipe(
        switchMap((link) => this._http.get(link, { responseType: 'blob' })),
        map((blob) => URL.createObjectURL(blob)),
        catchError(() => of(''))
      );
  }

  revoke(url: string | null | undefined): void {
    if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
  }
}
